import { ImportType } from 'dtos/imports/import.dto';

export interface ImportTypeInfo {
  type: ImportType;
  title: string;
  description: string;
  scope: 'store' | 'organization';
  // Template columns: required first. Example row shows the expected format.
  columns: string[];
  example: string[];
}

export const PRIMARY_IMPORTS: ImportTypeInfo[] = [
  {
    type: 'CATALOG',
    title: 'Product catalog & prices',
    description: 'Your POS pricebook or item list: barcode, name, category and shelf price. Add a cost and vendor column if you have them. Start here.',
    scope: 'store',
    columns: ['upc', 'name', 'retail_price', 'category', 'unit_cost', 'supplier_name', 'case_quantity'],
    example: ['611269991000', 'Red Bull 12oz', '3.29', 'Energy Drinks', '2.14', 'Metro Beverage', '24'],
  },
  {
    type: 'SALES',
    title: 'Sales',
    description: 'An item-level sales report from your POS. One row per item sold (or per item per day). Export at least the last 30 days.',
    scope: 'store',
    columns: ['business_date', 'upc', 'quantity', 'gross_sales', 'transaction_id', 'unit_price', 'discount_amount'],
    example: ['2026-10-31', '611269991000', '2', '6.58', 'T10001', '3.29', '0'],
  },
  {
    type: 'SUPPLIER_COSTS',
    title: 'Supplier costs',
    description: 'A vendor price list or cost update. Pellvr compares these costs to your shelf prices. Tip: you can also photograph invoices under Costs & Invoices.',
    scope: 'store',
    columns: ['upc', 'unit_cost', 'supplier_name', 'effective_date', 'case_cost', 'case_quantity'],
    example: ['611269991000', '2.14', 'Metro Beverage', '2026-10-15', '51.36', '24'],
  },
];

export const ADVANCED_IMPORTS: ImportTypeInfo[] = [
  { type: 'STORES', title: 'Stores', description: 'Add several stores at once.', scope: 'organization', columns: ['store_number', 'name', 'address_line_1', 'city', 'state', 'postal_code', 'timezone'], example: ['002', 'Elm St Fuel', '22 Elm St', 'Dallas', 'TX', '75201', 'America/Chicago'] },
  { type: 'PRODUCTS', title: 'Products only', description: 'Product master data without store prices.', scope: 'organization', columns: ['upc', 'name', 'unit_of_measure', 'sku', 'brand', 'category', 'case_quantity'], example: ['611269991000', 'Red Bull 12oz', 'EACH', 'RB12', 'Red Bull', 'Energy Drinks', '24'] },
  { type: 'STORE_PRODUCTS', title: 'Store prices', description: 'Shelf price and target margin per item for this store.', scope: 'store', columns: ['upc', 'retail_price', 'target_margin'], example: ['611269991000', '3.29', '0.35'] },
  { type: 'SUPPLIERS', title: 'Suppliers', description: 'Your vendor list.', scope: 'organization', columns: ['supplier_code', 'name', 'contact_name', 'phone', 'email'], example: ['METRO', 'Metro Beverage', 'Dana Ruiz', '2145550100', 'orders@metrobev.example'] },
  { type: 'PRODUCT_SUPPLIERS', title: 'Product–supplier links', description: 'Which vendor supplies each item, with their item code.', scope: 'store', columns: ['upc', 'supplier_code', 'supplier_sku', 'case_quantity', 'preferred'], example: ['611269991000', 'METRO', '88213', '24', 'true'] },
  { type: 'INVENTORY_COUNTS', title: 'Inventory counts', description: 'Physical count results.', scope: 'store', columns: ['count_date', 'upc', 'actual_quantity', 'count_reference'], example: ['2026-10-31', '611269991000', '18', 'Oct month-end'] },
];

export const ALL_IMPORTS = [...PRIMARY_IMPORTS, ...ADVANCED_IMPORTS];

export const importTypeLabel = (type: string) => ALL_IMPORTS.find(info => info.type === type)?.title ?? type;

export const FIELD_LABELS: Record<string, string> = {
  upc: 'UPC / barcode', sku: 'SKU / item code', name: 'Product name', retail_price: 'Shelf price', category: 'Category / department',
  subcategory: 'Subcategory', count_reference: 'Count reference', source_product_id: 'POS item ID', taxable: 'Taxable', brand: 'Brand', unit_cost: 'Unit cost', case_cost: 'Case cost', case_quantity: 'Units per case',
  supplier: 'Supplier / vendor name', supplier_name: 'Supplier / vendor name', supplier_code: 'Supplier code', supplier_sku: 'Supplier item code', unit_of_measure: 'Unit of measure',
  business_date: 'Sale date', quantity: 'Quantity sold', gross_sales: 'Sales amount', unit_price: 'Unit price', transaction_id: 'Transaction / receipt #',
  discount_amount: 'Discount', refund_amount: 'Refund', tax_total: 'Tax', effective_date: 'Effective date', source_reference: 'Invoice / reference #',
  store_number: 'Store number', target_margin: 'Target margin', count_date: 'Count date', actual_quantity: 'Counted quantity',
  expected_quantity: 'Expected quantity', contact_name: 'Contact name', phone: 'Phone', email: 'Email', preferred: 'Preferred supplier',
  address_line_1: 'Address', city: 'City', state: 'State', postal_code: 'ZIP', timezone: 'Time zone', active: 'Active',
};

export function templateCsv(info: ImportTypeInfo): string {
  const escape = (value: string) => /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return `${info.columns.join(',')}\n${info.example.map(escape).join(',')}\n`;
}
